import {
  InstrumentCategory,
  ConditionGrade,
  UserGrade,
  GradeInfo,
} from "../types";

// 악기 카테고리 상수
export const INSTRUMENT_CATEGORIES: {
  key: InstrumentCategory;
  label: string;
  icon: string;
}[] = [
  {
    key: "건반",
    label: "건반악기",
    icon: "🎹",
  },
  {
    key: "현악",
    label: "현악기",
    icon: "🎸",
  },
  {
    key: "관악",
    label: "관악기",
    icon: "🎺",
  },
  {
    key: "타악",
    label: "타악기",
    icon: "🥁",
  },
  {
    key: "국악",
    label: "국악기",
    icon: "🎵",
  },
  {
    key: "음향",
    label: "음향기기",
    icon: "🎧",
  },
  {
    key: "특수",
    label: "특수악기",
    icon: "🎻",
  },
  {
    key: "용품",
    label: "기타용품",
    icon: "🎼",
  },
];

// 상태 등급 상수
export const CONDITION_GRADES: {
  key: ConditionGrade;
  label: string;
  description: string;
  color: string;
}[] = [
  {
    key: "A",
    label: "A급",
    description: "사용감이 거의 없음",
    color: "text-blue-600",
  },
  {
    key: "B",
    label: "B급",
    description: "약간의 사용감 있음",
    color: "text-green-600",
  },
  {
    key: "C",
    label: "C급",
    description: "눈에 띄는 사용감",
    color: "text-yellow-600",
  },
  {
    key: "D",
    label: "D급",
    description: "많은 사용감이나 손상",
    color: "text-red-600",
  },
];

// 사용자 등급 상수
export const USER_GRADES: GradeInfo[] = [
  {
    grade: "C",
    name: "브론즈",
    description: "신규 회원",
    color: "text-orange-600",
    requirements: {
      safeTransactions: 0,
      averageRating: 0,
      disputeFree: true,
      totalTrades: 0,
    },
  },
  {
    grade: "B",
    name: "실버",
    description: "활성 회원",
    color: "text-gray-600",
    requirements: {
      safeTransactions: 5,
      averageRating: 4.0,
      disputeFree: true,
      totalTrades: 10,
    },
  },
  {
    grade: "A",
    name: "골드",
    description: "신뢰 회원",
    color: "text-yellow-600",
    requirements: {
      safeTransactions: 20,
      averageRating: 4.5,
      disputeFree: true,
      totalTrades: 50,
    },
  },
];

// 지역 상수
export const REGIONS = [
  "서울특별시",
  "부산광역시",
  "대구광역시",
  "인천광역시",
  "광주광역시",
  "대전광역시",
  "울산광역시",
  "세종특별자치시",
  "경기도",
  "강원도",
  "충청북도",
  "충청남도",
  "전라북도",
  "전라남도",
  "경상북도",
  "경상남도",
  "제주도",
];

// API 엔드포인트
export const API_ENDPOINTS = {
  AUTH: {
    LOGIN: "/api/auth/login",
    SIGNUP: "/api/auth/signup",
    LOGOUT: "/api/auth/logout",
    PROFILE: "/api/auth/profile",
  },
  PRODUCTS: {
    LIST: "/api/products",
    CREATE: "/api/products",
    DETAIL: (id: string) => `/api/products/${id}`,
    UPDATE: (id: string) => `/api/products/${id}`,
    DELETE: (id: string) => `/api/products/${id}`,
    SEARCH: "/api/products/search",
  },
  CHAT: {
    ROOMS: "/api/chat/rooms",
    MESSAGES: (roomId: string) => `/api/chat/rooms/${roomId}/messages`,
    CREATE_ROOM: "/api/chat/rooms",
  },
  UPLOAD: {
    IMAGES: "/api/upload/images",
  },
} as const;

// 페이지네이션 기본값
export const PAGINATION_DEFAULTS = {
  PAGE: 1,
  LIMIT: 20,
  MAX_LIMIT: 50,
} as const;

// 파일 업로드 제한
export const UPLOAD_LIMITS = {
  MAX_IMAGES: 10,
  MAX_FILE_SIZE: 5 * 1024 * 1024, // 5MB
  ALLOWED_IMAGE_TYPES: ["image/jpeg", "image/png", "image/webp"],
} as const;

// 배송 방법 상수
export const SHIPPING_TYPES = [
  { key: "direct", label: "직거래" },
  { key: "courier", label: "택배" },
  { key: "pickup", label: "픽업" },
];

// 앱 설정
export const APP_CONFIG = {
  NAME: "ConnecTone",
  DESCRIPTION: "AI 음악 분석 · 내 음악 피드백 · 레슨 플랫폼",
  VERSION: "1.0.0",
  SUPPORT_EMAIL: "help@connectone.com",
  KAKAO_CHANNEL_URL: "https://pf.kakao.com/_fyQwX",
  KAKAO_CHANNEL_ID: "connect-tone",
} as const;

// 피드백 관련 상수
export const FEEDBACK_CATEGORIES = {
  vocal: {
    label: "보컬",
    items: [
      { key: "pitch_accuracy", label: "음정 정확도" },
      { key: "rhythm_stability", label: "리듬 안정성" },
      { key: "breath_control", label: "호흡 조절" },
      { key: "tone_quality", label: "톤 퀄리티" },
      { key: "dynamics", label: "다이내믹스" },
      { key: "expression", label: "표현력" },
      { key: "vibrato", label: "비브라토" },
      { key: "articulation", label: "아티큘레이션" },
    ],
  },
  instrument: {
    label: "악기 연주",
    items: [
      { key: "technique", label: "테크닉" },
      { key: "rhythm_groove", label: "리듬/그루브" },
      { key: "timing", label: "타이밍" },
      { key: "dynamics_control", label: "다이내믹스 컨트롤" },
      { key: "tone_production", label: "톤 프로덕션" },
      { key: "musicality", label: "음악성" },
      { key: "interpretation", label: "해석력" },
      { key: "phrasing", label: "프레이징" },
    ],
  },
  composition: {
    label: "작곡/편곡",
    items: [
      { key: "melody", label: "멜로디" },
      { key: "harmony", label: "하모니" },
      { key: "arrangement", label: "편곡" },
      { key: "structure", label: "구조" },
      { key: "originality", label: "독창성" },
      { key: "songwriting", label: "작곡 기법" },
      { key: "progression", label: "코드 진행" },
      { key: "form", label: "형식" },
    ],
  },
  production: {
    label: "프로덕션",
    items: [
      { key: "mixing", label: "믹싱" },
      { key: "mastering", label: "마스터링" },
      { key: "recording_quality", label: "녹음 품질" },
      { key: "effects_usage", label: "이펙트 사용" },
      { key: "balance", label: "밸런스" },
      { key: "clarity", label: "명확성" },
      { key: "space", label: "공간감" },
      { key: "frequency_balance", label: "주파수 밸런스" },
    ],
  },
} as const;

export const STRENGTH_CATEGORIES = {
  vocal: {
    label: "보컬 강점",
    items: [
      { key: "pitch_perfect", label: "완벽한 음정" },
      { key: "rhythmic_feel", label: "탁월한 리듬감" },
      { key: "emotional_delivery", label: "감정 전달력" },
      { key: "vocal_range", label: "넓은 음역대" },
      { key: "tone_beauty", label: "아름다운 톤" },
      { key: "technical_control", label: "테크니컬 컨트롤" },
      { key: "stage_presence", label: "무대 존재감" },
      { key: "unique_voice", label: "독특한 목소리" },
    ],
  },
  instrument: {
    label: "악기 연주 강점",
    items: [
      { key: "technical_proficiency", label: "뛰어난 테크닉" },
      { key: "groove_feel", label: "그루브 감각" },
      { key: "musical_expression", label: "음악적 표현력" },
      { key: "improvisation", label: "즉흥 연주력" },
      { key: "tone_quality", label: "탁월한 톤" },
      { key: "timing_accuracy", label: "정확한 타이밍" },
      { key: "musicality", label: "음악성" },
      { key: "versatility", label: "다재다능함" },
    ],
  },
  composition: {
    label: "작곡 강점",
    items: [
      { key: "melodic_gift", label: "멜로디 창작력" },
      { key: "harmonic_sense", label: "하모니 감각" },
      { key: "creative_arrangement", label: "창의적 편곡" },
      { key: "song_structure", label: "곡 구조 이해" },
      { key: "original_ideas", label: "독창적 아이디어" },
      { key: "lyric_writing", label: "가사 쓰기" },
      { key: "production_sense", label: "프로덕션 감각" },
      { key: "genre_fusion", label: "장르 융합력" },
    ],
  },
} as const;
